import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sun, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAF9]" data-testid="forgot-password-page">
      <Seo title="Forgot password" description="Reset your HomeSchool Hub password." path="/forgot-password" noindex />

      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1770003354577-82b2a2d09d19" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/70 via-orange-400/40 to-transparent" />
        <div className="relative p-12 text-white h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-white text-amber-500"><Sun className="w-5 h-5" /></span>
            HomeSchool Hub
          </Link>
          <div className="font-display text-3xl font-semibold leading-tight max-w-md">
            Happens to the best of us. We&apos;ll get you back in.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-semibold tracking-tight">Reset your password</h1>
          <p className="mt-2 text-stone-500 text-sm">Enter the email you used to sign up and we&apos;ll send you a reset link.</p>

          {submitted ? (
            <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 p-5" data-testid="forgot-submitted">
              <div className="flex items-center gap-2 font-semibold text-emerald-800">
                <Mail className="w-4 h-4" /> Check your inbox
              </div>
              <p className="mt-2 text-sm text-emerald-800/90">
                If <strong>{email}</strong> matches an account, we just sent a reset link. It expires in <strong>30 minutes</strong>. Don&apos;t forget to peek in spam.
              </p>
              <Link to="/login" className="mt-4 inline-block text-sm font-medium text-amber-700 hover:text-amber-900" data-testid="forgot-back-to-login">
                ← Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 rounded-xl h-12"
                  autoFocus
                  data-testid="forgot-email-input"
                />
              </div>
              {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="forgot-error">{err}</div>
              )}
              <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-base" data-testid="forgot-submit">
                {loading ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" /> Sending…</>) : "Send reset link"}
              </Button>
              <div className="text-center text-sm text-stone-500">
                Remembered it? <Link to="/login" className="text-amber-700 hover:text-amber-900 font-medium">Back to login</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
